import React from 'react'
import styled from 'styled-components'

import { legalAgreementIds } from '../../../../utils/legalAgreements'
import { ClickWrapContentComponent } from '../../clickwrap/types'
import * as Styled from './styled'

const BAAContent: ClickWrapContentComponent<{ className?: string }> = ({ className }) => {
  return (
    <Styled.ContentContainer className={className}>
      <h1 className="text-center">Business Associate Agreement</h1>
      <p>
        THIS AGREEMENT TAKES EFFECT WHEN YOU CLICK THE “I AGREE” BUTTON BELOW (“EFFECTIVE DATE”). BY
        CLICKING ON THE “I AGREE” BUTTON BELOW YOU (A) ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND
        THIS AGREEMENT; (B) REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, POWER, AND AUTHORITY TO
        ENTER INTO THIS AGREEMENT AND, IF ENTERING INTO THIS AGREEMENT FOR AN ENTITY, THAT YOU HAVE
        THE LEGAL AUTHORITY TO BIND THAT ENTITY; AND (C) ACCEPT THIS AGREEMENT AND AGREE THAT YOU
        ARE LEGALLY BOUND BY ITS TERMS.
      </p>
      <p>
        This Business Associate Agreement (the “Agreement”) is a binding contract between the
        individual or entity that is the Designs for Health account holder (the “Covered Entity”)
        and Designs for Health, Inc. (the “Business Associate”). Covered Entity and Business
        Associate are entering into this Agreement because the Designs for Health account holder
        represented to Designs for Health, Inc. that it was a Covered Entity under HIPAA (defined
        below).
      </p>
      <p>
        This BAA is intended to ensure that Business Associate will establish and implement
        appropriate safeguards for the Protected Health Information (&quot;<strong>PHI</strong>
        &quot;) as required under the Health Insurance Portability and Accountability Act of 1996,
        as amended, and Information Technology for Economic and Clinical Health (HITECH) Act privacy
        and security provisions of the Stimulus Act, as amended, and their implementing regulations
        (collectively, &quot;<strong>HIPAA</strong>&quot;). Unless the context clearly indicates
        otherwise, the terms in this BAA shall have the same meaning as those terms defined under
        HIPAA Rules. Reference in this BAA to the Privacy Rule means the Privacy Rule, as set forth
        in the regulations at 45 C.F.R. Parts 160 and Subparts A and E of 164 (&quot;
        <strong>Privacy Rule</strong>&quot;) and to the Security Rule means the Security Rule, as
        set forth in the regulations at 45 C.F.R. Part 160 and Subparts A and C of Part 164 (
        <strong>“Security Rule”</strong>).
      </p>
      <h2>
        1. <strong>OBLIGATIONS OF BUSINESS ASSOCIATE.</strong>
      </h2>
      <p>
        1.1. Business Associate agrees not to use or disclose PHI, other than in connection with
        providing services to Covered Entity, as permitted or required by this BAA, and as Required
        By Law. Business Associate agrees to use appropriate safeguards to prevent use or disclosure
        of the PHI other than as provided for by this BAA.
      </p>
      <p>
        1.2. With respect to electronic PHI (“<strong>ePHI</strong>”), Business Associate agrees to
        implement appropriate and reasonable Administrative, Physical, and Technical Safeguards for
        the privacy and security of PHI to ensure the Integrity, Confidentiality and Availability
        and to prevent non-permitted uses and disclosures of PHI that it creates, receives,
        maintains, or transmits on behalf of Covered Entity.
      </p>
      <p>
        1.3. Business Associate agrees to mitigate, to the extent practicable, any harmful effect
        that is known to Business Associate as a result of a use or disclosure of PHI by Business
        Associate, its agents or Subcontractors, in violation of this BAA&rsquo;s requirements or
        that would otherwise cause a Breach of Unsecured PHI.
      </p>
      <p>
        1.4. The Business Associate agrees to report to Covered Entity any use or disclosure of PHI
        not provided for by this BAA, including any security incidents or other incidents that
        constitute a Breach of Unsecured PHI of which it becomes aware promptly, within ten (10)
        days of discovery. Such notice shall include the identification of each Individual whose
        Unsecured PHI has been or is reasonably believed by Business Associate to have been,
        accessed, acquired, or disclosed in connection with such Breach. In addition, Business
        Associate shall provide any additional information reasonably requested by Covered Entity
        for purposes of investigating the Breach and any other available information that Covered
        Entity is required to include to the Individual under 45 C.F.R. § 164.404(c) at the time of
        notification or promptly thereafter as information becomes available.
      </p>
      <p>
        1.5. Business Associate agrees to require that any Subcontractors that create, receive,
        maintain, or transmit PHI on behalf of the Business Associate to agree to the same
        restrictions, conditions, and requirements that apply to the Business Associate with respect
        to such information. If Business Associate becomes aware of a pattern of activity or
        practice of a Subcontractor that constitutes a material breach of their written business
        associate agreement, Business Associate shall take reasonable steps to cure the breach or
        end the violation, as applicable, and if such steps are unsuccessful, terminate the
        contract.
      </p>
      <p>
        1.6. Business Associate agrees to notify Covered Entity upon the receipt of a request from
        an Individual (or their authorized personal representative) for access to, amendment to, an
        accounting of disclosures of, a copy or electronic copy of, or a restriction on the use or
        disclosure of PHI.
      </p>
      <p>
        1.7. To the extent that Business Associate maintains PHI in a Designated Record Set on
        behalf of Covered Entity, Business Associate agrees to make available such PHI to the
        Covered Entity, or to an Individual upon written request as necessary to satisfy Covered
        Entity’s obligations under 45 C.F.R. § 164.524.
      </p>
      <p>
        1.8. To the extent that Business Associate maintains PHI in a Designated Record Set on
        behalf of Covered Entity, Business Associate agrees to make any amendments to PHI in a
        Designated Record Set as directed or agreed to by the Covered Entity pursuant to 45 C.F.R. §
        164.526.
      </p>
      <p>
        1.9. Business Associate agrees to maintain and make available the information required to
        provide an accounting of disclosures to the Covered Entity as necessary to satisfy Covered
        Entity’s obligations under 45 C.F.R. § 164.528.
      </p>
      <p>
        1.10. Business Associate agrees to make its internal practices, books, and records relating
        to the use and disclosure of PHI received from Covered Entity, or created or received by the
        Business Associate on behalf of Covered Entity, available to the Secretary of Health and
        Human Services for the purpose of the Secretary determining compliance with HIPAA.
      </p>
      <p>
        1.11. To the extent that Business Associate is to carry out one or more of Covered
        Entity&rsquo;s obligation(s) under the Privacy Rule, Business Associate agrees to comply
        with the requirements of the Privacy Rule that apply to the Covered Entity in the
        performance of such obligation(s).
      </p>
      <h2>
        2. <strong>PERMITTED USES AND DISCLOSURES BY BUSINESS ASSOCIATE.</strong>
      </h2>
      <p>
        2.1. Business Associate agrees to use or disclose PHI only as permitted or required for the
        purpose of performing services and obligations to Covered Entity, provided such use or
        disclosure of PHI would not violate HIPAA if done by Covered Entity, including the minimum
        necessary requirements under HIPAA, or violate the terms of this BAA.
      </p>
      <p>
        2.2. <span className="text-underline">Specific Use and Disclosure Provisions</span>
      </p>
      <p>
        (a) Except as otherwise limited in this BAA, Business Associate may use PHI for the proper
        management and administration of the Business Associate or to carry out its legal
        responsibilities, and may disclose PHI for these purposes provided that disclosures are
        either Required By Law or Business Associate obtains reasonable assurances from the person
        to whom the information is disclosed that it will remain confidential and used or further
        disclosed only as Required By Law or for the purpose for which it was disclosed to the
        person, and the person notifies the Business Associate of any instances of which it is aware
        in which the Confidentiality of the information has been breached.{' '}
      </p>
      <p>
        (b) Business Associate may use PHI: (a) to provide data aggregation services relating to the
        health care operations of Covered Entity; or (b) to create de-identified health information
        in accordance with 45 C.F.R. §164.514.
      </p>
      <p>
        (c) Business Associate may use PHI to report violations of law to appropriate Federal and
        State authorities, consistent with 45 C.F.R. §164.502(j)(1).
      </p>
      <h2>
        3. <strong>OBLIGATIONS OF COVERED ENTITY.</strong>
      </h2>
      <p>
        3.1. Covered Entity shall notify Business Associate of any limitation(s) in its Notice of
        Privacy Practices, to the extent that such limitation may affect Business Associate’s use or
        disclosure of PHI, and any further changes or limitations to such notice under 45 C.F.R. §
        164.520, to the extent that such changes or limitations may affect Business
        Associate&rsquo;s use or disclosure of PHI.
      </p>
      <p>
        3.2. Covered Entity shall notify Business Associate of any changes in, or revocation of,
        permission by an Individual to use or disclose PHI, to the extent that such changes may
        affect Business Associate’s use or disclosure of PHI.
      </p>
      <p>
        3.3. Covered Entity shall notify Business Associate of any restriction to the use or
        disclosure of PHI that Covered Entity has agreed to in accordance with 45 C.F.R. §164.522,
        to the extent that such restriction may affect Business Associate’s use or disclosure of
        PHI.
      </p>
      <p>
        3.4. Covered Entity shall not request Business Associate to use or disclose PHI in any
        manner that would not be permissible under the Privacy and Security Rule if done by Covered
        Entity, except as provided under this BAA.
      </p>
      <h2>
        4. <strong>TERM AND TERMINATION</strong>
      </h2>
      <p>
        4.1. This BAA shall be in effect as of the Effective Date and shall terminate on the earlier
        of the date that: (a) Either party terminates for cause; or (b) All of the PHI received from
        Covered Entity, or created or received by Business Associate on behalf of Covered Entity, is
        destroyed or returned to Covered Entity. If it is not feasible to return or destroy PHI,
        protections shall be extended in accordance with this Section. Upon either party&rsquo;s
        knowledge of material breach by the other party, the non-breaching party shall provide an
        opportunity for the breaching party to cure. If the breaching party does not cure the breach
        or end the violation within a reasonable timeframe not to exceed thirty (30) calendar days
        from the notification of the breach, or if a material term of the BAA has been breached and
        a cure is not possible, the non-breaching party may terminate this BAA upon written notice
        to the other party.
      </p>
      <p>
        4.2. Upon termination of this BAA for any reason, Business Associate, with respect to PHI
        received from Covered Entity, or created, maintained, or received by Business Associate on
        behalf of Covered Entity, shall:
      </p>
      <p>
        (a) Retain only that PHI that is necessary for Business Associate to continue its proper
        management and administration or to carry out its legal responsibilities.
      </p>
      <p>
        (b) Return to Covered Entity or, if agreed to by Covered Entity, destroy the remaining PHI
        that the Business Associate still maintains in any form. If it is not feasible to return or
        destroy PHI, the protections under this BAA shall continue to be extended by Business
        Associate; provided, however, that Business Associate may use and disclose Patient
        Information retained by Business Associate after termination of this Agreement only for
        those purposes that make return or destruction of such Patient Information infeasible.
      </p>
      <p>
        (c) Continue to use appropriate safeguards and comply with Subpart C of 45 C.F.R. Part 164
        with respect to ePHI to prevent use or disclosure of the PHI, other than as provided for in
        this Section, for as long as Business Associate retains the PHI.
      </p>
      <p>
        (d) Not use or disclose the PHI retained by Business Associate other than for the purposes
        for which such PHI was retained and subject to the same conditions set out at Section 2.2(a)
        above which applied prior to termination, and return to Covered Entity or, if agreed to by
        Covered Entity, destroy the PHI retained by Business Associate when it is no longer needed
        for such purposes.
      </p>
      <p>
        4.3. The obligations of Business Associate under this Section shall survive the termination
        of this BAA.
      </p>
      <h2>
        5. <strong>MISCELLANEOUS</strong>.
      </h2>
      <p>
        5.1. The parties agree to take such action as is necessary to amend this BAA to comply with
        the requirements of HIPAA and any other applicable law.
      </p>
      <p>
        5.2. This BAA will be binding on the successors and assigns of the Covered Entity and the
        Business Associate. However, this BAA may not be assigned, in whole or in part, without the
        written consent of the other party. Any attempted assignment in violation of this provision
        shall be null and void.
      </p>
    </Styled.ContentContainer>
  )
}

BAAContent.documentKey = legalAgreementIds.baa

const StyledBAAContent = styled(BAAContent)``

export default StyledBAAContent
